/**
 * Message Types
 *
 * TypeScript interfaces for chat message components.
 */

import firebase from "firebase/compat/app"
import Message from "src/app/models/Message"
import Customer from "src/app/models/Customer"
import User from "src/app/models/User"

/**
 * Base props shared by all message components
 */
export interface BaseMessageProps {
  message: Message
  bgChat: string
  txChat: string
  isTabletOrMobile: boolean
}

/**
 * Extended props for message components that need reply support
 */
export interface MessageWithReplyProps extends BaseMessageProps {
  replyMessageID?: string
  customer?: Customer
  phoneNumber?: string
}

/**
 * Props for the main ChatMessage component
 */
export interface ChatMessageProps {
  message: Message
  index: number
  customer?: Customer
  user?: User
  messages: Message[]
  collaborationId: string
  companyID: string
  debug: boolean
  phoneNumber?: string
  lastUserInteraction?: firebase.firestore.Timestamp
}

/**
 * Props for text-based messages
 */
export interface TextMessageProps extends MessageWithReplyProps {
  debug?: boolean
}

/**
 * Props for document messages
 */
export interface DocumentMessageProps extends MessageWithReplyProps {
  onSave?: () => void
}

/**
 * Props for media messages (image, video, sticker)
 */
export interface MediaMessageProps extends MessageWithReplyProps {
  allMediaInRoom?: MediaItem[]
  previousMedia?: string
  nextMedia?: string
}

/**
 * Props for audio messages
 */
export interface AudioMessageProps extends MessageWithReplyProps {}

/**
 * Props for location messages
 */
export interface LocationMessageProps extends MessageWithReplyProps {}

/**
 * Props for order/invoice messages
 */
export interface OrderMessageProps extends BaseMessageProps {}

/**
 * Props for template messages
 */
export interface TemplateMessageProps extends BaseMessageProps {}

/**
 * Media item for gallery navigation
 * Includes all metadata needed for proper download functionality
 */
export interface MediaItem {
  id: string
  type: "image" | "video" | "sticker" | "image_with_text"
  url: string
  name: string
  thumbnail: string
  // Additional metadata for download support:
  filename?: string      // Preferred filename
  fileRecordId?: string  // Fallback filename (FileRecord ID)
  mimeType?: string      // For extension mapping
  extension?: string     // Explicit extension
  duration?: string      // For video/audio (format: "MM:SS")
  size?: number         // File size in bytes
}

/**
 * Message type enum for switch statements
 */
export type MessageType =
  | "text"
  | "button"
  | "faq_liveagent"
  | "product"
  | "document"
  | "image"
  | "image_with_text"
  | "video"
  | "sticker"
  | "audio"
  | "location"
  | "order"
  | "template"
  | "interactive"
  | "unsupported"

/**
 * Utility function to normalize message type
 */
export function normalizeMessageType(type: string | undefined): MessageType {
  if (!type) return "unsupported"

  const normalized = type.toLowerCase()

  switch (normalized) {
    case "text":
    case "button":
    case "faq_liveagent":
    case "product":
      return normalized as MessageType
    case "document":
      return "document"
    case "image":
    case "image_with_text":
      return normalized as MessageType
    case "video":
      return "video"
    case "sticker":
      return "sticker"
    case "audio":
      return "audio"
    case "location":
      return "location"
    case "order":
    case "order_card":
      return "order"
    case "template":
    case "interactive":
      return "template"
    default:
      return "unsupported"
  }
}

/**
 * Check if message type is a media type
 */
export function isMediaMessage(type: string | undefined): boolean {
  if (!type) return false
  const t = type.toLowerCase()
  return ["image", "image_with_text", "video", "sticker"].includes(t)
}

/**
 * Check if message type is a text-based type
 */
export function isTextMessage(type: string | undefined): boolean {
  if (!type) return false
  const t = type.toLowerCase()
  return ["text", "button", "faq_liveagent"].includes(t)
}
